<script setup lang="ts">
	import { useFormContainer } from "~/composables/useFormContainer";

	import FormCard from "~/components/ui/FormCard.vue";
	import IdentityCard from "~/components/inputs/IdentityCard.vue";
	import InputFloatingLabel from "~/components/inputs/InputFloatingLabel.vue";
	import AutoComplete from "~/components/inputs/AutoComplete.vue";
	import Accordion from "~/components/ui/Accordion.vue";
	import BackButton from "~/components/inputs/BackButton.vue";
	import SelectDate from "~/components/inputs/SelectDate.vue";
	import jsonCities from "~/data/cities.json";
	import type { IValue } from "~/interfaces";
	import { searchCity } from "~/utils/searchAutocomplete";
	import type { Ref } from "vue";
	import Validator from "~/utils/validator";
	import { useVuelidate } from "@vuelidate/core";
	import identityFormRules from "~/rules/identityDocData";
	import { DateTime } from "luxon";
import Perks from "~/components/static/Perks.vue";

	defineComponent({
		name: "IdentityDocData",
	});

	const { extLocale } = defineProps({
		progress: { type: Number, required: true },
		title: { type: String, required: true },
		extLocale: { type: Boolean, default: false },
		stickyButton: { type: Boolean, default: false }
	});

	const { locale } = await useLocale("forms", "identity_card", extLocale);

	const { FormContainer } = useFormContainer();

	onMounted(() => {
		emit("init");
	});

	const identityType = useState<number | null>("identityType", () => null);
	const documentNumber = useState<string | null>("documentNumber", () => null);
	const documentCity = useState<string | null>("documentCity", () => null);
	const documentExpire = useState<string | null>("documentExpire", () => null);
	const documentRelease = useState<string | null>("documentRelease", () => null);

	const accordion = ref();
	const accordion2 = ref();

	const docEnum = {
		[DOCS.IDENTITY_CARD]: "identity_card",
		[DOCS.PASSPORT]: "passport",
		[DOCS.DRIVERS_LICENSE]: "drivers_license",
	};
	const currentNumDocHelpers = computed(() => {
		const key = docEnum[identityType.value ?? 0];
		return locale.value?.document_help_options[key].num;
	});
	const currentLocDocHelpers = computed(() => {
		const key = docEnum[identityType.value ?? 0];
		return locale.value?.document_help_options[key].loc;
	});

	const userDate = computed(() => {
		const { year, month, day } = userTaxCode();
		return { day, year, month };
	});

	const minYearRelease = computed(() => {
		const currentDate = DateTime.now();
		const minDate = currentDate.minus({ years: 11 })
		return minDate.year
	})

	const maxYearExpire = computed(() => {
		if (!documentRelease.value) return undefined;
		const releaseDate = DateTime.fromFormat(documentRelease.value, 'dd/MM/yyyy')
		if (releaseDate > DateTime.now()) return undefined;
		return releaseDate.plus({years: 11}).year;
	});

	const minYearExpire = computed(() => {
		const currentYear = DateTime.now();
		return currentYear.year;
	});

	const emit = defineEmits(["init", "submit", "goBack", "change"]);

	const v$ = useVuelidate(identityFormRules, {
		identityType,
		documentNumber,
		documentCity,
		documentExpire,
		documentRelease,
	});

	const validate = () => {
		v$.value.$touch();
		v$.value.$validate();
		return !v$.value.$error;
	};

	const resetForm = () => {
		if (accordion.value && accordion.value.showItems) {
			accordion.value.toggleAccordion();
		}
		if (accordion2.value && accordion2.value.showItems) {
			accordion2.value.toggleAccordion();
		}

		documentNumber.value = "";
		documentCity.value = "";
		documentExpire.value = "";
		documentRelease.value = "";
		v$.value.$reset();
	};

	const resetDocumentExpire = () => {
		documentExpire.value = ""
	};

	const submit = () => {
		if (validate()) {
			emit("submit");
		}
	};

	const change = () => {
		emit("change");
		resetForm();
	};

	watch(identityType, resetForm);

	watch(documentRelease, (val) => {
		if (!val) resetDocumentExpire()
		const releaseDate = DateTime.fromFormat(val!, 'dd/MM/yyyy')
		if (releaseDate > DateTime.now()) 
			resetDocumentExpire()
		if (maxYearExpire.value && maxYearExpire.value < releaseDate.year)
			resetDocumentExpire()
	})

</script>

<template>
	<FormContainer :progress="progress">
	    <FormCard :title="locale?.title" :has-submit="!!identityType" @submit="submit" :has-sticky-button="stickyButton">
			<template #before>
				<div class="flex flex-wrap justify-between px-4 sm:px-8">
					<p class="pageSubtitle" v-html="locale?.text" />
					<div class="w-full flex justify-center items-center">
						<IdentityCard
							v-model="identityType"
							@change="change()"
						></IdentityCard>
					</div>
				</div>
			</template>

			<template v-if="identityType">
				<h1 class="pageTitle mb-4">{{ locale?.title_inputs  }}</h1>
				<div class="w-full lg:w-1/2 lg:pr-4 mb-3">
					<InputFloatingLabel
						v-if="identityType == DOCS.IDENTITY_CARD"
						v-model="documentNumber"
						type="text"
						:mask="identityCardMask"
						:label="locale?.inputs.document_number"
						:class="`${identityType != DOCS.IDENTITY_CARD ? 'hidden' : ''}`"
						:error="v$.documentNumber.$error"
						:error-message="Validator.message(v$.documentNumber)"
						test-id="documentNumber"
					/>

					<InputFloatingLabel
						v-if="identityType == DOCS.PASSPORT"
						v-model="documentNumber"
						type="text"
						:mask="passportMask"
						:label="locale?.inputs.document_number"
						:class="`${identityType != DOCS.PASSPORT ? 'hidden' : ''}`"
						:error="v$.documentNumber.$error"
						:error-message="Validator.message(v$.documentNumber)"
						test-id="documentNumber"
					/>

					<InputFloatingLabel
						v-if="identityType == DOCS.DRIVERS_LICENSE"
						v-model="documentNumber"
						type="text"
						:mask="drivingLicenseMask"
						:label="locale?.inputs.document_number"
						:error="v$.documentNumber.$error"
						:error-message="Validator.message(v$.documentNumber)"
						test-id="documentNumber"
					/>

					<Accordion ref="accordion">
						<p class="text-sm" v-html="locale?.inputs.document_help_num" />

						<template #items>
							<div class="yellowBox my-5">
								<div
									v-for="(fig, i) in currentNumDocHelpers"
									:key="i"
									class="flex flex-col text-xs"
								>
									<p class="mb-4 sm:h-10" v-html="fig.text" />
									<img
										v-if="fig.img"
										:src="fig.img"
										class="w-full mx-auto max-w-xs mb-4"
										alt=""
									/>
								</div>
							</div>
						</template>
					</Accordion>
				</div>
				<div class="w-full lg:w-1/2 mb-3">
					<AutoComplete
						v-model="documentCity"
						:searchFunction="searchCity"
						:label="locale?.inputs.document_city"
						:error="v$.documentCity.$error"
						:error-message="Validator.message(v$.documentCity)"
						:clear-incomplete="true"
						test-id="documentCity"
					/>
					<Accordion ref="accordion2">
						<p class="text-sm " v-html="locale?.inputs.document_help_loc" />
						<template #items>
							<div class="yellowBox my-5">
								<div
									v-for="(fig, i) in currentLocDocHelpers"
									:key="i"
									class="flex flex-col text-xs"
								>
									<p class="mb-4 sm:h-10" v-html="fig.text" />
									<img
										v-if="fig.img"
										:src="fig.img"
										class="w-full mx-auto max-w-xs mb-4"
									/>
								</div>
							</div>
						</template>
					</Accordion>
				</div>

				<div class="w-full lg:w-1/2 lg:pr-4 mb-4">
					<label v-if="identityType == 1" class="text-xs">{{
						locale?.release_date
					}}</label>
					<label v-if="identityType == 3" class="text-xs">{{
						locale?.release_date_passport_point_7
					}}</label>
					<label v-if="identityType == 4" class="text-xs">{{
						locale?.release_date_point_4a
					}}</label>
					<SelectDate
						v-model="documentRelease"
						:start-year="minYearRelease ?? 1900"
						:error="v$.documentRelease.$error"
						:error-message="Validator.message(v$.documentRelease)"
						test-id="documentRelease"
					/>
				</div>

				<div class="w-full lg:w-1/2 lg:pr-4 mb-4">
					<label v-if="identityType == 1" class="text-xs">{{
						locale?.expiration_date
					}}</label>
					<label v-if="identityType == 3" class="text-xs">{{
						locale?.expiration_date_passport_point_8
					}}</label>
					<label v-if="identityType == 4" class="text-xs">{{
						locale?.expiration_date_point_4b
					}}</label>
					<SelectDate
						v-model="documentExpire"
						:start-year="minYearExpire"
						:end-year="maxYearExpire"
						:disabled="!minYearExpire || !maxYearExpire"
						:error="v$.documentExpire.$error"
						:error-message="Validator.message(v$.documentExpire)"
						test-id="documentExpire"
					/>
				</div>
			</template>
			<template #link v-if="identityType">
				<BackButton :event="`goBack`" @go-back="$emit('goBack')" />
			</template>
		</FormCard>
	</FormContainer>
	<Perks />
</template>

<style scoped></style>
